import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Business } from '@sbclite/core';
import { api } from '../services/api';

interface Envelope<T> {
  success: boolean;
  data: T;
  message?: string;
}

export const businessCallHandlingQueryKeys = {
  all: ['business'] as const,
  customers: () => [...businessCallHandlingQueryKeys.all, 'customers'] as const,
  membershipsByUser: (userId: string | undefined) => [...businessCallHandlingQueryKeys.all, 'memberships', 'user', userId] as const,
  membershipsByCustomer: (customerId: string | undefined) => [...businessCallHandlingQueryKeys.all, 'memberships', 'customer', customerId] as const,
  promptsByCustomer: (customerId: string | undefined) => [...businessCallHandlingQueryKeys.all, 'prompts', customerId] as const,
  policiesByCustomer: (customerId: string | undefined) => [...businessCallHandlingQueryKeys.all, 'policies', customerId] as const,
  policy: (policyId: string | undefined) => [...businessCallHandlingQueryKeys.all, 'policy', policyId] as const,
  promptAssignments: (policyId: string | undefined) => [...businessCallHandlingQueryKeys.all, 'prompt-assignments', policyId] as const,
  auditLogs: (customerId: string | undefined, limit: number) => [...businessCallHandlingQueryKeys.all, 'audit-logs', customerId, limit] as const,
};

export function useBusinessCustomers() {
  return useQuery({
    queryKey: businessCallHandlingQueryKeys.customers(),
    queryFn: async () => {
      const response = await api.get<Envelope<Business.CustomerInfo[]>>('/api/business/customers');
      return response.data.data;
    },
  });
}

export function useCreateBusinessCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: Business.CreateCustomerInput) => {
      const response = await api.post<Envelope<Business.CustomerInfo>>('/api/business/customers', input);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: businessCallHandlingQueryKeys.customers() });
    },
  });
}

export function useCreateCustomerMembership() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: Business.CreateMembershipInput) => {
      const response = await api.post<Envelope<Business.CustomerMembershipInfo>>('/api/business/memberships', input);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: businessCallHandlingQueryKeys.membershipsByUser(variables.userId) });
      if (variables.customerId) {
        queryClient.invalidateQueries({ queryKey: businessCallHandlingQueryKeys.membershipsByCustomer(variables.customerId) });
      }
    },
  });
}

export function useCustomerMemberships(customerId: string | undefined) {
  return useQuery({
    queryKey: businessCallHandlingQueryKeys.membershipsByCustomer(customerId),
    queryFn: async () => {
      const response = await api.get<Envelope<Business.CustomerMembershipInfo[]>>(`/api/business/customers/${customerId}/memberships`);
      return response.data.data;
    },
    enabled: customerId !== undefined,
  });
}

export function useUserMemberships(userId: string | undefined) {
  return useQuery({
    queryKey: businessCallHandlingQueryKeys.membershipsByUser(userId),
    queryFn: async () => {
      const response = await api.get<Envelope<Business.CustomerMembershipInfo[]>>(`/api/business/memberships/user/${userId}`);
      return response.data.data;
    },
    enabled: userId !== undefined,
  });
}

export function useAnnouncementPrompts(customerId: string | undefined) {
  return useQuery({
    queryKey: businessCallHandlingQueryKeys.promptsByCustomer(customerId),
    queryFn: async () => {
      const response = await api.get<Envelope<Business.AnnouncementPromptInfo[]>>(`/api/business/customers/${customerId}/prompts`);
      return response.data.data;
    },
    enabled: customerId !== undefined,
  });
}

export function useCreateAnnouncementPrompt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: Business.CreatePromptInput) => {
      const response = await api.post<Envelope<Business.AnnouncementPromptInfo>>('/api/business/prompts', input);
      return response.data;
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: businessCallHandlingQueryKeys.promptsByCustomer(response.data.customerId) });
    },
  });
}

export interface UploadAnnouncementPromptInput {
  readonly customerId: string;
  readonly file: File;
  readonly displayName?: string;
  readonly codec?: Business.UploadedPromptCodec;
}

export function useUploadAnnouncementPrompt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ customerId, file, displayName, codec }: UploadAnnouncementPromptInput) => {
      const form = new FormData();
      form.append('file', file);
      if (displayName !== undefined && displayName.trim().length > 0) form.append('displayName', displayName.trim());
      if (codec !== undefined) form.append('codec', codec);
      const response = await api.post<Envelope<Business.AnnouncementPromptInfo>>(`/api/business/customers/${customerId}/prompts/upload`, form);
      return response.data;
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: businessCallHandlingQueryKeys.promptsByCustomer(response.data.customerId) });
      queryClient.invalidateQueries({ queryKey: businessCallHandlingQueryKeys.auditLogs(response.data.customerId, 100) });
    },
  });
}

export function useUpdateAnnouncementPrompt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }: Business.UpdatePromptInput) => {
      const response = await api.patch<Envelope<Business.AnnouncementPromptInfo>>(`/api/business/prompts/${id}`, updates);
      return response.data;
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: businessCallHandlingQueryKeys.promptsByCustomer(response.data.customerId) });
    },
  });
}

export function useBusinessCallHandlingPolicies(customerId: string | undefined) {
  return useQuery({
    queryKey: businessCallHandlingQueryKeys.policiesByCustomer(customerId),
    queryFn: async () => {
      const response = await api.get<Envelope<Business.BusinessCallHandlingPolicyInfo[]>>(`/api/business/customers/${customerId}/policies`);
      return response.data.data;
    },
    enabled: customerId !== undefined,
  });
}

export function useBusinessCallHandlingPolicy(policyId: string | undefined) {
  return useQuery({
    queryKey: businessCallHandlingQueryKeys.policy(policyId),
    queryFn: async () => {
      const response = await api.get<Envelope<Business.BusinessCallHandlingPolicyInfo>>(`/api/business/policies/${policyId}`);
      return response.data.data;
    },
    enabled: policyId !== undefined,
  });
}

export function useCreateBusinessCallHandlingPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: Business.CreatePolicyInput) => {
      const response = await api.post<Envelope<Business.BusinessCallHandlingPolicyInfo>>('/api/business/policies', input);
      return response.data;
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: businessCallHandlingQueryKeys.policiesByCustomer(response.data.customerId) });
      queryClient.invalidateQueries({ queryKey: businessCallHandlingQueryKeys.policy(response.data.id) });
    },
  });
}

export function useUpdateBusinessCallHandlingPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }: Business.UpdatePolicyInput) => {
      const response = await api.patch<Envelope<Business.BusinessCallHandlingPolicyInfo>>(`/api/business/policies/${id}`, updates);
      return response.data;
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: businessCallHandlingQueryKeys.policiesByCustomer(response.data.customerId) });
      queryClient.invalidateQueries({ queryKey: businessCallHandlingQueryKeys.policy(response.data.id) });
      queryClient.invalidateQueries({ queryKey: businessCallHandlingQueryKeys.auditLogs(response.data.customerId, 100) });
    },
  });
}

export function usePromptAssignments(policyId: string | undefined) {
  return useQuery({
    queryKey: businessCallHandlingQueryKeys.promptAssignments(policyId),
    queryFn: async () => {
      const response = await api.get<Envelope<Business.BusinessCallHandlingPromptAssignmentInfo[]>>(`/api/business/policies/${policyId}/prompts`);
      return response.data.data;
    },
    enabled: policyId !== undefined,
  });
}

export function useAssignPromptToPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ policyId, ...input }: Business.AssignPromptInput) => {
      const response = await api.post<Envelope<Business.BusinessCallHandlingPromptAssignmentInfo>>(`/api/business/policies/${policyId}/prompts`, input);
      return response.data;
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: businessCallHandlingQueryKeys.promptAssignments(response.data.policyId) });
      queryClient.invalidateQueries({ queryKey: businessCallHandlingQueryKeys.policy(response.data.policyId) });
    },
  });
}

export function useEnableBusinessCallHandlingOverride() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ policyId, promptId, reason }: Pick<Business.EnableOverrideInput, 'policyId' | 'promptId' | 'reason'>) => {
      const response = await api.post<Envelope<Business.BusinessCallHandlingPolicyInfo>>(`/api/business/policies/${policyId}/override/enable`, { promptId, reason });
      return response.data;
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: businessCallHandlingQueryKeys.policy(response.data.id) });
      queryClient.invalidateQueries({ queryKey: businessCallHandlingQueryKeys.policiesByCustomer(response.data.customerId) });
      queryClient.invalidateQueries({ queryKey: businessCallHandlingQueryKeys.auditLogs(response.data.customerId, 100) });
    },
  });
}

export function useDisableBusinessCallHandlingOverride() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (policyId: string) => {
      const response = await api.post<Envelope<Business.BusinessCallHandlingPolicyInfo>>(`/api/business/policies/${policyId}/override/disable`);
      return response.data;
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: businessCallHandlingQueryKeys.policy(response.data.id) });
      queryClient.invalidateQueries({ queryKey: businessCallHandlingQueryKeys.policiesByCustomer(response.data.customerId) });
      queryClient.invalidateQueries({ queryKey: businessCallHandlingQueryKeys.auditLogs(response.data.customerId, 100) });
    },
  });
}

export function useBusinessCallHandlingAuditLogs(customerId: string | undefined, limit = 100) {
  return useQuery({
    queryKey: businessCallHandlingQueryKeys.auditLogs(customerId, limit),
    queryFn: async () => {
      const response = await api.get<Envelope<Business.BusinessCallHandlingAuditLogInfo[]>>(`/api/business/customers/${customerId}/audit-logs`, { limit });
      return response.data.data;
    },
    enabled: customerId !== undefined,
  });
}
